import { useQuery } from '@tanstack/react-query';
import { CalendarClock, MapPin, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatRange } from '@/components/appointments';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import type { PortalAppointment } from '@/lib/types';

/** Anstehende Termine im Kundenportal (nur wenn vorhanden). */
export function PortalAppointmentsCard() {
  const { data } = useQuery({ queryKey: ['portal', 'appointments'], queryFn: () => api<{ items: PortalAppointment[] }>('/portal/appointments') });
  const upcoming = (data?.items ?? []).filter((a) => new Date(a.endAt) > new Date());
  if (upcoming.length === 0) return null;
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarClock className="size-4" /> Ihre Termine
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {upcoming.map((a) => (
          <div key={a.id} className="rounded-md border p-3 text-sm">
            <p className="flex flex-wrap items-center gap-2 font-medium">
              <span className={a.status === 'CANCELLED' ? 'line-through' : undefined}>{a.title}</span>
              {a.status === 'CANCELLED' && <Badge variant="destructive">abgesagt</Badge>}
            </p>
            <p className="text-muted-foreground">{formatRange(a)}</p>
            {a.technicians.length > 0 && <p className="text-muted-foreground">mit {a.technicians.join(', ')}</p>}
            {a.location && (
              <p className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="size-3.5" /> {a.location}
              </p>
            )}
            {a.onlineMeetingUrl && a.status === 'CONFIRMED' && (
              <a href={a.onlineMeetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <Video className="size-3.5" /> Online-Besprechung beitreten
              </a>
            )}
            {(a.ticket || a.repair) && (
              <p className="mt-1 flex gap-3">
                {a.ticket && (
                  <Link to={`/portal/tickets/${a.ticket.id}`} className="text-primary hover:underline">
                    Anfrage {a.ticket.number}
                  </Link>
                )}
                {a.repair && (
                  <Link to={`/portal/repairs/${a.repair.id}`} className="text-primary hover:underline">
                    Reparatur {a.repair.number}
                  </Link>
                )}
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
